import React, { useEffect, useMemo, useState } from "react";
import {
  getAdminProfile,
  getAdminContents,
  getAnalyticsSummary,
} from "../../api/adminApi";
import "../../styles/admin.css";
import { Link } from "react-router-dom";

const formatFaNumber = (value) => {
  return new Intl.NumberFormat("fa-IR").format(value || 0);
};

const normalizeContentsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.data?.contents)) return data.data.contents;
  return [];
};

function AdminDashboard() {
  const [staff, setStaff] = useState(null);
  const [contents, setContents] = useState([]);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [error, setError] = useState("");
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const token = localStorage.getItem("staffToken");

        if (!token) {
          window.location.href = "/admin/login";
          return;
        }

        setStatsLoading(true);

        const profileData = await getAdminProfile();
        setStaff(profileData.staff);

        if (profileData.staff) {
          localStorage.setItem("staffUser", JSON.stringify(profileData.staff));
        }

        const [contentsData, analyticsData] = await Promise.all([
          getAdminContents(),
          getAnalyticsSummary(),
        ]);

        setContents(normalizeContentsResponse(contentsData));
        setAnalyticsSummary(analyticsData);
      } catch (err) {
        setError(err.message || "خطا در دریافت اطلاعات داشبورد");

        localStorage.removeItem("staffToken");
        localStorage.removeItem("staffUser");

        setTimeout(() => {
          window.location.href = "/admin/login";
        }, 1200);
      } finally {
        setStatsLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const stats = useMemo(() => {
    const publishedContents = contents.filter(
      (item) => item?.status === "published"
    );

    return {
      news: publishedContents.filter((item) => item?.type === "news").length,
      courses: publishedContents.filter((item) => item?.type === "course").length,
      totalSiteVisits: analyticsSummary?.totalSiteVisits || 0,
      todaySiteVisits: analyticsSummary?.todaySiteVisits || 0,
      totalContentViews: analyticsSummary?.totalContentViews || 0,
      todayContentViews: analyticsSummary?.todayContentViews || 0,
      mostViewedContents: analyticsSummary?.mostViewedContents || [],
    };
  }, [contents, analyticsSummary]);

  const handleLogout = () => {
    localStorage.removeItem("staffToken");
    localStorage.removeItem("staffUser");
    window.location.href = "/admin/login";
  };

  const isEditor = staff?.role === "editor";
  const canViewContactMessages = Boolean(staff) && !isEditor;

  return (
    <div className="admin-panel-page">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <h2>چشم هوشمند آموزش</h2>
          <span>پنل مدیریت</span>
        </div>

        <nav className="admin-menu">
          <Link to="/admin/dashboard" className="active">
            داشبورد
          </Link>

          <Link to="/admin/content" className="admin-sidebar-link">
            مدیریت محتوا
          </Link>

          <Link to="/admin/content/archive" className="admin-sidebar-link">
            آرشیو مطالب
          </Link>

          {canViewContactMessages && (
            <Link
              to="/admin/contact-messages"
              className="admin-sidebar-link"
            >
              پیام‌های تماس
            </Link>
          )}

          <Link to="/admin/content" className="admin-sidebar-link">
            انتشار محتوا
          </Link>

          <Link to="/admin/change-password">
            تغییر رمز عبور
          </Link>
        </nav>

        <button className="admin-logout-btn" onClick={handleLogout}>
          خروج
        </button>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div>
            <h1>داشبورد مدیریت</h1>
            <p>مدیریت اخبار، دوره‌های آموزشی و محتوای سایت</p>
          </div>

          {staff && (
            <div className="admin-user-box">
              <strong>
                {staff.firstName} {staff.lastName}
              </strong>
              <span>{staff.role}</span>
            </div>
          )}
        </header>

        {error && <div className="admin-error">{error}</div>}

        <section className="admin-cards-grid">
          <div className="admin-stat-card">
            <span>اخبار منتشرشده</span>
            <strong>
              {statsLoading ? "..." : formatFaNumber(stats.news)}
            </strong>
            <p>خبرهای منتشرشده در سایت</p>
          </div>

          <div className="admin-stat-card">
            <span>دوره‌های منتشرشده</span>
            <strong>
              {statsLoading ? "..." : formatFaNumber(stats.courses)}
            </strong>
            <p>دوره‌های آموزشی منتشرشده در سایت</p>
          </div>

          <div className="admin-stat-card">
            <span>کل بازدید سایت</span>
            <strong>
              {statsLoading ? "..." : formatFaNumber(stats.totalSiteVisits)}
            </strong>
            <p>بازدیدهای ثبت‌شده از صفحات سایت</p>
          </div>

          <div className="admin-stat-card">
            <span>بازدید امروز سایت</span>
            <strong>
              {statsLoading ? "..." : formatFaNumber(stats.todaySiteVisits)}
            </strong>
            <p>بازدیدهای ثبت‌شده از ابتدای امروز</p>
          </div>

          <div className="admin-stat-card">
            <span>کل بازدید مطالب</span>
            <strong>
              {statsLoading ? "..." : formatFaNumber(stats.totalContentViews)}
            </strong>
            <p>مجموع بازدید خبرها و دوره‌ها</p>
          </div>

          <div className="admin-stat-card">
            <span>بازدید مطالب امروز</span>
            <strong>
              {statsLoading ? "..." : formatFaNumber(stats.todayContentViews)}
            </strong>
            <p>بازدید خبرها و دوره‌ها در امروز</p>
          </div>

          <div className="admin-stat-card">
            <span>وضعیت حساب</span>
            <strong>فعال</strong>
            <p>دسترسی شما به پنل فعال است</p>
          </div>
        </section>

        <section className="admin-welcome-card">
          <h2>پربازدیدترین محتواها</h2>

          {stats.mostViewedContents.length > 0 ? (
            <div className="admin-content-list">
              {stats.mostViewedContents.map((content) => (
                <Link
                  key={content._id || content.slug}
                  to={`/${content.type === "course" ? "courses" : "news"}/${content.slug}`}
                  className="admin-dashboard-card"
                >
                  <h3>{content.title}</h3>
                  <p>
                    {content.type === "course" ? "دوره آموزشی" : "خبر"} | بازدید:{" "}
                    {formatFaNumber(content.viewsCount)}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <p>هنوز بازدیدی برای محتواها ثبت نشده است.</p>
          )}
        </section>

        <section className="admin-welcome-card">
          <h2>خوش آمدید</h2>
          <p>
            از این بخش می‌توانید اخبار فناوری و دوره‌های آموزشی سایت را مدیریت
            کنید.
          </p>
        </section>

        <Link to="/admin/content" className="admin-dashboard-card">
          <h3>مدیریت اخبار و دوره‌ها</h3>
          <p>افزودن، ویرایش، حذف و انتشار محتوای سایت</p>
        </Link>

        <Link to="/admin/content/archive" className="admin-dashboard-card">
          <h3>آرشیو مطالب</h3>
          <p>مشاهده همه محتواهای تولیدشده توسط شما</p>
        </Link>

        {canViewContactMessages && (
          <Link
            to="/admin/contact-messages"
            className="admin-dashboard-card"
          >
            <h3>پیام‌های تماس</h3>

            <p>
              مشاهده و مدیریت پیام‌های ارسال‌شده کاربران
            </p>
          </Link>
        )}
      </main>
    </div>
  );
}

export default AdminDashboard;
